// ProductionStepLogs.js - "Historique de production" for M9: the full
// cross-order ProductionStepLog traceability ledger (who moved which
// production run to which step, and when), separate from the inline
// per-order "Historique" on the Production page which only shows one
// order's history at a time. Mirrors RawMaterialMovements.js's / M7's
// StockMovements.js's conventions.
import React, { useEffect, useState } from "react";
import "../Stock/Stock.css";
import { ToastContainer, toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  TextField,
} from "@mui/material";
import { UilAngleLeft, UilAngleRight } from "@iconscout/react-unicons";
import { userRequest } from "../../RequestMethods";
import { getColorDisplayName } from "../../utils/colorNames";

const STATUS_OPTIONS = [
  "Lancement du tracé + préparation du fichier",
  "Coupe (sous-traitance)",
  "Tirage film",
  "Impression",
  "Fourniture (billet, côte, emballage, élastique)",
  "Livré (vers un autre atelier)",
  "Confection",
  "Contrôle qualité",
];

const ProductionStepLogs = () => {
  const [productionOrders, setProductionOrders] = useState([]);
  const [stepLogs, setStepLogs] = useState([]);
  const [stepLogsLoading, setStepLogsLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ productionOrderId: "", toStatus: "" });

  const fetchProductionOrders = async () => {
    try {
      const res = await userRequest.get("production-orders");
      setProductionOrders(res.data.productionOrders || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des ordres de production.");
    }
  };

  const fetchStepLogs = async (page = 1) => {
    try {
      setStepLogsLoading(true);
      const params = { page, limit: pagination.limit };
      if (filters.productionOrderId) params.productionOrderId = filters.productionOrderId;
      if (filters.toStatus) params.toStatus = filters.toStatus;

      const res = await userRequest.get("production-orders/step-logs", { params });
      setStepLogs(res.data.stepLogs || []);
      setPagination(res.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement de l'historique.");
    } finally {
      setStepLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchProductionOrders();
    fetchStepLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    fetchStepLogs(1);
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) fetchStepLogs(pagination.page - 1);
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) fetchStepLogs(pagination.page + 1);
  };

  const optionLabel = (po) => {
    const productLabel = po.productTitle || po.productId;
    return `${productLabel}${po.color ? ` — ${getColorDisplayName(po.color)}` : ""} / ${po.size}`;
  };

  return (
    <div className="Stock">
      <h1>Historique de production</h1>

      <section className="stock-section">
        <form className="stock-filters" onSubmit={handleApplyFilters} style={{ alignItems: "center" }}>
          <Autocomplete
            options={productionOrders}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            renderOption={(optionProps, option) => {
              const { key, ...rest } = optionProps;
              return <li key={option._id} {...rest}>{optionLabel(option)}</li>;
            }}
            value={productionOrders.find((po) => po._id === filters.productionOrderId) || null}
            onChange={(e, val) => setFilters({ ...filters, productionOrderId: val ? val._id : "" })}
            renderInput={(params) => (
              <TextField {...params} label="Ordre de production" placeholder="Rechercher..." size="small" />
            )}
            sx={{ minWidth: 280 }}
          />
          <select value={filters.toStatus} onChange={(e) => setFilters({ ...filters, toStatus: e.target.value })}>
            <option value="">Toutes les étapes</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="submit" className="action-icon-btn btn-primary">Filtrer</button>
        </form>

        {stepLogsLoading ? (
          <div className="loading-container">
            <div className="loading-spinner-custom"></div>
          </div>
        ) : (
          <>
            <TableContainer component={Paper} className="stock-table-container">
              <Table>
                <TableHead>
                  <TableRow className="table-header">
                    <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                    <TableCell>Produit</TableCell>
                    <TableCell>Couleur / Taille</TableCell>
                    <TableCell>Qté</TableCell>
                    <TableCell>Transition</TableCell>
                    <TableCell>Par</TableCell>
                    <TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stepLogs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">Aucun historique trouvé.</TableCell>
                    </TableRow>
                  )}
                  {stepLogs.map((log) => (
                    <TableRow key={log._id}>
                      <TableCell>{new Date(log.createdAt).toLocaleString("fr-FR")}</TableCell>
                      <TableCell>{log.productTitle || "—"}</TableCell>
                      <TableCell>
                        {log.productionOrderId?.color ? getColorDisplayName(log.productionOrderId.color) : "—"} / {log.productionOrderId?.size || "—"}
                      </TableCell>
                      <TableCell>{log.productionOrderId?.quantity ?? "—"}</TableCell>
                      <TableCell>{log.fromStatus ? `${log.fromStatus} → ${log.toStatus}` : `Créé : ${log.toStatus}`}</TableCell>
                      <TableCell>
                        {log.performedBy ? `${log.performedBy.name || ""} ${log.performedBy.surname || ""}`.trim() : "—"}
                      </TableCell>
                      <TableCell>{log.note || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <div className="stock-pagination">
              <button
                className="pagination-btn" onClick={handlePrevPage}
                disabled={pagination.page <= 1}
              >
                <UilAngleLeft /> Précédent
              </button>
              <span>
                Page {pagination.page} / {pagination.pages || 1} ({pagination.total} entrées)
              </span>
              <button
                className="pagination-btn" onClick={handleNextPage}
                disabled={pagination.page >= pagination.pages}
              >
                Suivant <UilAngleRight />
              </button>
            </div>
          </>
        )}
      </section>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default ProductionStepLogs;
